import moment from "moment";
import { AutopodderResultRow, ScheduleDataItem } from "../types";
import { PlanWarning } from "./batchJobWarnings";

export interface ConstraintValidationInput {
    poddedData: AutopodderResultRow[];
    slotSelectionDict: { [slotId: string]: string[] };
    scheduleData: { [scheduleName: string]: ScheduleDataItem };
    originalSelectionCounts: { [scheduleName: string]: number };
}

/**
 * Orchestrator: runs all 7 constraint checks and returns combined warnings.
 */
export function validateAllConstraints(input: ConstraintValidationInput): PlanWarning[] {
    return [
        ...checkSlotOverfill(input),
        ...checkDuplicateAdInSlot(input),
        ...checkMinSeparation(input),
        ...checkAdvertiserSeparation(input),
        ...checkAdvertiserMaxPerDay(input),
        ...checkMaxPerDay(input),
        ...checkUnitsRemaining(input),
    ];
}

/**
 * 1. Slot Overfill: total ad length in a slot exceeds SIZE_MAX.
 */
export function checkSlotOverfill(input: ConstraintValidationInput): PlanWarning[] {
    const { poddedData, slotSelectionDict, scheduleData } = input;
    const warnings: PlanWarning[] = [];

    for (const slot of poddedData) {
        const adNames = slotSelectionDict[slot.ID] || [];
        if (adNames.length === 0) continue;

        let totalLength = 0;
        for (const adName of adNames) {
            const item = scheduleData[adName];
            if (item && item.UNITLENGTH) {
                totalLength += item.UNITLENGTH;
            }
        }

        if (totalLength > slot.SIZE_MAX) {
            warnings.push({
                type: 'Slot Overfill',
                message: `Slot ${slot.SLOT_ID} is overfilled: ${totalLength}s of ads in a ${slot.SIZE_MAX}s slot (${totalLength - slot.SIZE_MAX}s over).`,
                source: 'constraint-check',
                slotId: slot.ID,
                severity: 'error',
            });
        }
    }

    return warnings;
}

/**
 * 2. Duplicate Ad in Slot: same scheduleName appears 2+ times in one slot.
 */
export function checkDuplicateAdInSlot(input: ConstraintValidationInput): PlanWarning[] {
    const { poddedData, slotSelectionDict } = input;
    const warnings: PlanWarning[] = [];

    for (const slot of poddedData) {
        const adNames = slotSelectionDict[slot.ID] || [];
        if (adNames.length < 2) continue;

        const counts: { [name: string]: number } = {};
        for (const name of adNames) {
            counts[name] = (counts[name] || 0) + 1;
        }

        for (const [name, count] of Object.entries(counts)) {
            if (count >= 2) {
                warnings.push({
                    type: 'Duplicate Ad in Slot',
                    message: `"${name}" appears ${count} times in slot ${slot.SLOT_ID}.`,
                    scheduleName: name,
                    source: 'constraint-check',
                    slotId: slot.ID,
                    severity: 'error',
                });
            }
        }
    }

    return warnings;
}

/** Helper: build a list of { scheduleName, slotId, slotSlotId, dateTime } for all placements. */
function buildPlacementList(input: ConstraintValidationInput): {
    scheduleName: string;
    slotId: string;
    slotSlotId: string;
    dateTime: moment.Moment;
    advertiserId: string;
    advSep: number;
}[] {
    const { poddedData, slotSelectionDict, scheduleData } = input;
    const placements: {
        scheduleName: string;
        slotId: string;
        slotSlotId: string;
        dateTime: moment.Moment;
        advertiserId: string;
        advSep: number;
    }[] = [];

    for (const slot of poddedData) {
        const adNames = slotSelectionDict[slot.ID] || [];
        const dt = moment(slot.DATE_TIME);
        for (const adName of adNames) {
            const item = scheduleData[adName];
            placements.push({
                scheduleName: adName,
                slotId: slot.ID,
                slotSlotId: slot.SLOT_ID,
                dateTime: dt,
                advertiserId: item?.ADVERTISER_ID || '',
                advSep: item?.ADV_SEP || 0,
            });
        }
    }

    return placements;
}

/**
 * 3. Min Separation: same ad in two slots closer than MIN_SEP minutes apart.
 * Groups by scheduleName, sorts by time, checks consecutive pairs.
 */
export function checkMinSeparation(input: ConstraintValidationInput): PlanWarning[] {
    const { scheduleData } = input;
    const warnings: PlanWarning[] = [];
    const placements = buildPlacementList(input);

    // Group by scheduleName
    const byAd: { [name: string]: typeof placements } = {};
    for (const p of placements) {
        if (!byAd[p.scheduleName]) byAd[p.scheduleName] = [];
        byAd[p.scheduleName].push(p);
    }

    for (const [name, group] of Object.entries(byAd)) {
        if (group.length < 2) continue;

        const minSep = scheduleData[name]?.MIN_SEP;
        if (!minSep || minSep <= 0) continue;

        // Sort by dateTime ascending
        group.sort((a, b) => a.dateTime.valueOf() - b.dateTime.valueOf());

        for (let i = 0; i < group.length - 1; i++) {
            const diffMinutes = group[i + 1].dateTime.diff(group[i].dateTime, 'minutes');
            if (diffMinutes < minSep) {
                warnings.push({
                    type: 'Min Separation',
                    message: `"${name}" in slots ${group[i].slotSlotId} and ${group[i + 1].slotSlotId} are ${diffMinutes} min apart (min ${minSep} min required).`,
                    scheduleName: name,
                    source: 'constraint-check',
                    severity: 'error',
                });
            }
        }
    }

    return warnings;
}

/**
 * 4. Advertiser Separation: two ads from same ADVERTISER_ID closer than ADV_SEP minutes apart.
 * Uses Math.max of ADV_SEP values when two ads have different values.
 */
export function checkAdvertiserSeparation(input: ConstraintValidationInput): PlanWarning[] {
    const warnings: PlanWarning[] = [];
    const placements = buildPlacementList(input);

    // Group by advertiserId
    const byAdvertiser: { [advId: string]: typeof placements } = {};
    for (const p of placements) {
        if (!p.advertiserId) continue;
        if (!byAdvertiser[p.advertiserId]) byAdvertiser[p.advertiserId] = [];
        byAdvertiser[p.advertiserId].push(p);
    }

    for (const [advId, group] of Object.entries(byAdvertiser)) {
        if (group.length < 2) continue;

        // Sort by dateTime ascending
        group.sort((a, b) => a.dateTime.valueOf() - b.dateTime.valueOf());

        for (let i = 0; i < group.length - 1; i++) {
            const a = group[i];
            const b = group[i + 1];
            const requiredSep = Math.max(a.advSep, b.advSep);
            if (requiredSep <= 0) continue;

            const diffMinutes = b.dateTime.diff(a.dateTime, 'minutes');
            if (diffMinutes < requiredSep) {
                const advName = input.scheduleData[a.scheduleName]?.ADVERTISER_NAME || advId;
                warnings.push({
                    type: 'Advertiser Separation',
                    message: `Advertiser "${advName}": "${a.scheduleName}" (slot ${a.slotSlotId}) and "${b.scheduleName}" (slot ${b.slotSlotId}) are ${diffMinutes} min apart (min ${requiredSep} min required).`,
                    source: 'constraint-check',
                    severity: 'error',
                });
            }
        }
    }

    return warnings;
}

/** Helper: normalize day-of-week string to 3-letter abbreviation. */
function normalizeDay(day: string): string {
    const dayLower = day.toLowerCase().trim();
    const dayMap: { [key: string]: string } = {
        'mon': 'Mon', 'monday': 'Mon',
        'tue': 'Tue', 'tuesday': 'Tue',
        'wed': 'Wed', 'wednesday': 'Wed',
        'thu': 'Thu', 'thursday': 'Thu',
        'fri': 'Fri', 'friday': 'Fri',
        'sat': 'Sat', 'saturday': 'Sat',
        'sun': 'Sun', 'sunday': 'Sun',
    };
    return dayMap[dayLower] || day;
}

/**
 * 5. Advertiser Max Per Day: more ads from same advertiser on a day than ADV_MAX_PER_DAY_CALC allows.
 */
export function checkAdvertiserMaxPerDay(input: ConstraintValidationInput): PlanWarning[] {
    const { poddedData, slotSelectionDict, scheduleData } = input;
    const warnings: PlanWarning[] = [];

    // Count per advertiser per day-of-week
    // Key: `${advertiserId}|${dayOfWeek}`
    const counts: { [key: string]: { count: number; limit: number; advName: string } } = {};

    for (const slot of poddedData) {
        const adNames = slotSelectionDict[slot.ID] || [];
        const day = normalizeDay(slot.DAY_OF_WEEK || '');
        if (!day) continue;

        for (const adName of adNames) {
            const item = scheduleData[adName];
            if (!item || !item.ADVERTISER_ID) continue;
            const advMax = item.ADV_MAX_PER_DAY_CALC;
            if (advMax === undefined || advMax === null || advMax <= 0) continue;

            const key = `${item.ADVERTISER_ID}|${day}`;
            if (!counts[key]) {
                counts[key] = { count: 0, limit: advMax, advName: item.ADVERTISER_NAME || item.ADVERTISER_ID };
            }
            counts[key].count++;
        }
    }

    for (const [key, { count, limit, advName }] of Object.entries(counts)) {
        if (count > limit) {
            const [, day] = key.split('|');
            warnings.push({
                type: 'Advertiser Max Per Day',
                message: `Advertiser "${advName}" has ${count} ads on ${day} (max ${limit} per day).`,
                source: 'constraint-check',
                severity: 'error',
            });
        }
    }

    return warnings;
}

/**
 * 6. Max Per Day: same ad placed more times on a day than MAX_PER_DAY allows.
 */
export function checkMaxPerDay(input: ConstraintValidationInput): PlanWarning[] {
    const { poddedData, slotSelectionDict, scheduleData } = input;
    const warnings: PlanWarning[] = [];

    // Count per ad per day-of-week
    // Key: `${scheduleName}|${dayOfWeek}`
    const counts: { [key: string]: number } = {};

    for (const slot of poddedData) {
        const adNames = slotSelectionDict[slot.ID] || [];
        const day = normalizeDay(slot.DAY_OF_WEEK || '');
        if (!day) continue;

        for (const adName of adNames) {
            const key = `${adName}|${day}`;
            counts[key] = (counts[key] || 0) + 1;
        }
    }

    for (const [key, count] of Object.entries(counts)) {
        const [adName, day] = key.split('|');
        const item = scheduleData[adName];
        if (!item) continue;

        const maxPerDay = item.MAX_PER_DAY;
        if (maxPerDay !== undefined && maxPerDay !== null && maxPerDay > 0 && count > maxPerDay) {
            warnings.push({
                type: 'Max Per Day',
                message: `"${adName}" is placed ${count} times on ${day} (max ${maxPerDay} per day).`,
                scheduleName: adName,
                source: 'constraint-check',
                severity: 'error',
            });
        }
    }

    return warnings;
}

/**
 * 7. Units Remaining: ad placed more times than available units.
 * Available = UNITS_REMAINING + originalCount (units consumed by the autopodder's original plan
 * that are already accounted for in UNITS_REMAINING).
 */
export function checkUnitsRemaining(input: ConstraintValidationInput): PlanWarning[] {
    const { poddedData, slotSelectionDict, scheduleData, originalSelectionCounts } = input;
    const warnings: PlanWarning[] = [];

    // Count total placements per ad across all slots in current strategy
    const currentCounts: { [name: string]: number } = {};
    const currentStrategySlotIds = new Set(poddedData.map(slot => slot.ID));

    for (const [slotId, adNames] of Object.entries(slotSelectionDict)) {
        if (!currentStrategySlotIds.has(slotId)) continue;
        for (const adName of adNames) {
            currentCounts[adName] = (currentCounts[adName] || 0) + 1;
        }
    }

    for (const [adName, currentCount] of Object.entries(currentCounts)) {
        const item = scheduleData[adName];
        if (!item) continue;

        const originalCount = originalSelectionCounts[adName] || 0;
        const available = item.UNITS_REMAINING + originalCount;

        if (currentCount > available) {
            warnings.push({
                type: 'Units Remaining',
                message: `"${adName}" is placed ${currentCount} times but only ${available} units available.`,
                scheduleName: adName,
                source: 'constraint-check',
                severity: 'error',
            });
        }
    }

    return warnings;
}
